import { Component, ChangeDetectionStrategy, Input, ElementRef, signal, OnInit, inject, computed } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { StudentEngagementOrg } from "../StudentEngagementOrg";
import SeOrg = StudentEngagementOrg.SeOrg;
import { S25ItemI } from "../../../pojo/S25ItemI";
import { SeOrganizationTileComponent } from "../organization/se.organization.tile.component";
import { S25States } from "../../../services/state/state.map";
import { StudentEngagementService } from "../student.engagement.service";
import { Attribute } from "../../s25-event/EventMicroI";
import { SeEditableLogo } from "../se.editable.logo.component";
import { Item } from "../../../pojo/Item";
import { UserprefService } from "../../../services/userpref.service";

export interface SeEvent extends S25ItemI {
    date: string;
    locations?: string[];
    imageAttr?: Attribute;
    description?: string;
    hostOrg?: SeOrg;
    occurrences?: { date: string; locations: string[] }[];
}

@TypeManagerDecorator("s25-ng-se-event-details-view")
@Component({
    selector: "s25-ng-se-event-details-view",
    template: `
        @if (init()) {
            @if (eventDetails()) {
                <div class="event-header">
                    <se-editable-logo
                        [itemId]="+itemId"
                        [itemTypeId]="evTypeId"
                        [imageAttr]="eventDetails().imageAttr"
                        [canEdit]="canEdit()"
                    ></se-editable-logo>

                    <div class="event-info">
                        <h1 class="event-title">{{ eventDetails().itemName }}</h1>

                        <div class="event-details-grid">
                            <div class="detail-item">
                                <span class="detail-label">Date & Time</span>
                                <div class="detail-content">
                                    <s25-ng-icon [type]="'calendarDate'" [size]="20" class="detail-icon"></s25-ng-icon>
                                    <span class="detail-value">{{ eventDetails().date }}</span>
                                </div>
                            </div>

                            @if (eventDetails().locations?.length) {
                                <div class="detail-item">
                                    <span class="detail-label">Locations</span>
                                    @for (location of eventDetails().locations; track location) {
                                        <div class="detail-content">
                                            <s25-ng-icon
                                                [type]="'locationMarker'"
                                                [size]="20"
                                                class="detail-icon"
                                            ></s25-ng-icon>
                                            <span class="detail-value">{{ location }}</span>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
                @if (errorMsg()) {
                    <p class="ngRed">{{ errorMsg() }}</p>
                }
                @if (eventDetails().description) {
                    <div class="event-section description-section">
                        <h2>About This Event</h2>
                        <div class="description-content">
                            {{ eventDetails().description }}
                        </div>
                    </div>
                }
                @if (eventDetails().occurrences?.length) {
                    <div class="event-section">
                        <h2>Occurrences</h2>
                        <div class="occurrences-list">
                            @for (occurrence of eventDetails().occurrences; track occurrence) {
                                <div class="occurrence-group">
                                    <div class="occurrence-date">
                                        <s25-ng-icon
                                            [type]="'calendarDate'"
                                            [size]="20"
                                            class="detail-icon"
                                        ></s25-ng-icon>
                                        <span class="detail-value">{{ occurrence.date }}</span>
                                    </div>
                                    @if (occurrence.locations.length) {
                                        <div class="occurrence-locations">
                                            @for (location of occurrence.locations; track location) {
                                                <div class="location-item">
                                                    <s25-ng-icon
                                                        [type]="'locationMarker'"
                                                        [size]="18"
                                                        class="detail-icon location-indent"
                                                    ></s25-ng-icon>
                                                    <span class="detail-value">{{ location }}</span>
                                                </div>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                }
                @if (eventDetails().hostOrg) {
                    <div class="event-section">
                        <h2>Hosted By</h2>
                        <se-organization-tile
                            [org]="eventDetails().hostOrg"
                            [routingStateName]="stateMap.studentEngagementOrganization"
                        ></se-organization-tile>
                    </div>
                }
            } @else {
                <p class="noResults">Event details could not be found</p>
            }
        } @else {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        }
    `,
    styles: `
        :host {
            display: block;
            margin-bottom: 2rem;
        }

        /* Event Header Section */
        .event-header {
            display: flex;
            gap: 3rem;
            align-items: flex-start;
            margin-bottom: 3rem;
        }

        .event-info {
            flex: 1;
            min-width: 0;
        }

        .event-title {
            font-size: 2.8rem;
            color: #1a1a1a;
            margin: 0 0 2rem 0;
            font-weight: 800;
            line-height: 1.2;
        }

        .event-details-grid {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }

        .detail-icon {
            flex-shrink: 0;
            margin-top: 0.2rem;
            color: #2196f3;
        }

        .detail-content {
            display: flex;
            align-items: center;
            gap: 0.25rem;
        }

        .detail-label {
            font-size: 0.875rem;
            color: #666;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .detail-value {
            font-size: 1.125rem;
            color: #333;
            position: relative;
            top: 2px;
        }

        /* Occurrences Section Styling */
        .occurrences-list {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 1.5rem;
            margin-top: 1rem;
        }

        .occurrence-group {
            background: #ffffff;
            padding: 0;
            border-radius: 12px;
            border: 1px solid #e0e0e0;
            box-shadow:
                0 2px 4px rgba(0, 0, 0, 0.04),
                0 4px 8px rgba(0, 0, 0, 0.02);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            overflow: hidden;
        }

        .occurrence-group:hover {
            transform: translateY(-2px);
            box-shadow:
                0 4px 12px rgba(0, 0, 0, 0.08),
                0 8px 16px rgba(0, 0, 0, 0.04);
            border-color: #2196f3;
        }

        .occurrence-date {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            padding: 1rem 1.25rem;
            background: linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%);
            border-bottom: 1px solid #e3f2fd;
            font-weight: 600;
            color: #1565c0;
        }

        .occurrence-date .detail-icon {
            color: #1976d2;
        }

        .occurrence-date .detail-value {
            font-size: 1rem;
            color: #1565c0;
        }

        .occurrence-locations {
            padding: 1.25rem;
            display: flex;
            flex-direction: column;
            gap: 0.875rem;
        }

        .location-item {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            padding: 0.75rem 1rem;
            background: #f8f9fa;
            border-radius: 8px;
            transition: all 0.2s ease;
            border: 1px solid transparent;
        }

        .location-item:hover {
            background: #ffffff;
            border-color: #e0e0e0;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
        }

        .location-indent {
            color: #2196f3;
            flex-shrink: 0;
            margin-top: 2px;
        }

        .location-item .detail-value {
            color: #424242;
            font-size: 0.95rem;
            line-height: 1.5;
        }

        /* Event Sections */
        .event-section {
            margin-bottom: 3rem;
        }

        .event-section h2 {
            color: #333;
            font-size: 1.8rem;
            margin: 0 0 1.5rem 0;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #f0f0f0;
        }

        se-organization-tile {
            display: block;
            width: min(30em, 100%);
        }

        /* Description Section */
        .description-content {
            color: #444;
            line-height: 1.8;
            font-size: 1.1rem;
            white-space: pre-wrap;
        }

        ::ng-deep .host-logo-placeholder svg {
            color: #667eea;
            opacity: 0.7;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
            :host {
                padding: 1rem;
            }

            .event-header {
                flex-direction: column;
                gap: 2rem;
                text-align: center;
            }

            .event-title {
                font-size: 2rem;
            }

            .event-details-grid {
                align-items: center;
            }

            .detail-item {
                justify-content: center;
            }

            .event-section {
                padding: 1.5rem;
            }
        }

        @media (max-width: 480px) {
            .event-title {
                font-size: 1.75rem;
            }

            .event-section h2 {
                font-size: 1.5rem;
            }

            .description-content {
                font-size: 1rem;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent, S25LoadingInlineModule, SeOrganizationTileComponent, SeEditableLogo],
    providers: [StudentEngagementService],
})
export class SeEventDetailsViewComponent implements OnInit {
    @Input() itemId: number;

    eventDetails = signal<SeEvent>(null);

    private seService = inject(StudentEngagementService);

    init = signal(false);
    errorMsg = signal("");
    canEdit = computed(() => this.seService.hasEditRights());
    itemIdSetter: (val: number) => void;
    datePrefs: { date: string; dateTime: string; time: string };
    protected stateMap = S25States.names;
    protected evTypeId = Item.Ids.Event;

    constructor(private elementRef: ElementRef) {
        this.elementRef.nativeElement.angBridge = this;
        this.itemIdSetter = (value: number) => {
            if (this.itemId !== +value) {
                this.itemId = +value;
                return this.initComponent();
            }
        };
    }

    async ngOnInit() {
        await this.initComponent();
    }

    private async initComponent() {
        this.init.set(false);
        this.seService.setGroupId(await UserprefService.getGroupId());
        this.eventDetails.set(await StudentEngagementService.getEventDetails(this.itemId));
        this.init.set(true);
    }
}
